// Halventra invisible collector - public entrypoint.
//
// Usage (auto-boot from the loader script tag):
//   <script src="https://cdn.halventra.com/v1/loader.js"
//           data-site-key="hv_site_xxx" data-endpoint="https://api.halventra.com"></script>
//
// Programmatic:
//   const hv = halventra.init({ siteKey, endpoint });
//   const { token } = await hv.execute("login");   // call before submitting the form
//   // send `token` to your backend, which calls POST /siteverify with its secret key.
import { Collectors } from "./collectors";
import { BeaconResponse, sendBeacon } from "./beacon";

export interface HalventraConfig {
  siteKey: string;
  endpoint?: string; // base URL of the data plane, default https://api.halventra.com
}

function randomSessionId(): string {
  const c: any = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && c.randomUUID) return c.randomUUID().replace(/-/g, "");
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

export class Halventra {
  private collectors = new Collectors();
  private sessionId = randomSessionId();
  private endpoint: string;
  private siteKey: string;
  private lastToken: string | null = null;

  constructor(config: HalventraConfig) {
    this.siteKey = config.siteKey;
    this.endpoint = (config.endpoint || "https://api.halventra.com").replace(/\/$/, "");
    this.collectors.start(Date.now());
  }

  // Flush current signals for an action and obtain a fresh challenge token.
  async execute(action: string): Promise<BeaconResponse | null> {
    const summary = this.collectors.summary(Date.now());
    const res = await sendBeacon(`${this.endpoint}/ingest`, this.siteKey, this.sessionId, action, summary);
    if (res) this.lastToken = res.token;
    return res;
  }

  get token(): string | null {
    return this.lastToken;
  }

  get session(): string {
    return this.sessionId;
  }
}

export function init(config: HalventraConfig): Halventra {
  return new Halventra(config);
}

// Auto-boot from the loader <script data-site-key=...> tag, if present.
function autoBoot(): void {
  if (typeof document === "undefined") return;
  const el = document.currentScript as HTMLScriptElement | null;
  const tag =
    el && el.dataset && el.dataset.siteKey
      ? el
      : (document.querySelector("script[data-site-key]") as HTMLScriptElement | null);
  if (!tag) return;
  const siteKey = tag.dataset.siteKey as string;
  const endpoint = tag.dataset.endpoint;
  const instance = init({ siteKey, endpoint });
  (window as any).halventra = Object.assign((window as any).halventra || {}, {
    init,
    instance,
    execute: (action: string) => instance.execute(action),
  });
}

if (typeof window !== "undefined") {
  (window as any).halventra = (window as any).halventra || { init };
  autoBoot();
}
